import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React, { useEffect, useRef, useState } from 'react';
import { handleModelListAutoScroll } from '../helpers/ofertas-scroll';
import { addCarlineInPath, cleanCarlineInPath, hasCarlineInPath } from '../helpers/ofertas-url-manager';
import { useOfertasTracker } from '../helpers/use-ofertas-tracker';
import { CarlineWithOfertas, Oferta } from '../services/ofertas-service/models/ofertas';

gsap.registerPlugin(ScrollTrigger);

const getInitialCarlineIndex = (carlines: CarlineWithOfertas[]) => {
	if (!carlines || carlines.length === 0) {
		return -1;
	}

	const carlineUrlSplitted = window.location.pathname.split('/');
	const carlineUrl = carlineUrlSplitted[carlineUrlSplitted.length - 1].replace('.app', '');
	const carlineIndex = carlines.findIndex((carline) => carline.name === carlineUrl);

	return carlineIndex >= 0 ? carlineIndex : 0;
};

export const useOfertasState = (carlines: CarlineWithOfertas[]) => {
	const [activeCarlineIndex, setActiveCarlineIndex] = useState<number>(() => getInitialCarlineIndex(carlines));
	const [selectedOferta, setSelectedOferta] = useState<Oferta | undefined>(() => {
		const initialIndex = getInitialCarlineIndex(carlines);

		if (initialIndex < 0) {
			return undefined;
		}

		return carlines[initialIndex]?.ofertas?.[0];
	});
	const numberOfCarlines = carlines.length;
	const prevNumberOfCarlinesRef = useRef<number>(numberOfCarlines);

	const ofertasRef = useRef<HTMLDivElement | null>(null);
	const isAutoScrollingRef = useRef(false);
	const disableScrollDetection = useRef(false);
	const isFilterAnchorPendingRef = useRef(false);

	const carlineBasicVehicleLoad = useRef(new Set());
	const carlineSelectedURLRef = React.useRef<boolean>(hasCarlineInPath(carlines));

	// Guardamos la referencia de ScrollTrigger
	const scrollTriggerRef = useRef<ScrollTrigger | null>(null);

	const { trackOfertasVehicleLoad } = useOfertasTracker();

	const getOfertasTopOffset = () => (window.innerWidth < 960 ? 50 : 64);

	const scrollOfertasToTop = () => {
		const rect = ofertasRef.current?.getBoundingClientRect();
		if (!rect) {
			return;
		}

		window.scrollTo({
			top: Math.max(0, rect.top + window.scrollY - getOfertasTopOffset()),
			behavior: 'auto', // 'auto' para evitar animación de scroll
		});
	};

	const anchorScrollOnFilter = () => {
		isFilterAnchorPendingRef.current = true;
		disableScrollDetection.current = true;
		carlineSelectedURLRef.current = false;
		cleanCarlineInPath();
		gsap.killTweensOf(window);

		if (window.innerWidth >= 960) {
			scrollOfertasToTop();
		}
	};

	/* ---------------------------------------------------------------
       1. Control de Scroll (scroll manual)
    ------------------------------------------------------------------ */
	const isScrollTriggerReady = activeCarlineIndex >= 0;

	useEffect(() => {
		if (!ofertasRef.current || numberOfCarlines <= 0 || !isScrollTriggerReady) return;

		// limpiar instancias previas
		if (scrollTriggerRef.current) scrollTriggerRef.current.kill();

		//  ScrollTrigger reemplaza a 'useScroll' de Framer Motion
		scrollTriggerRef.current = ScrollTrigger.create({
			trigger: ofertasRef.current,
			start: 'top top',
			end: 'bottom bottom',
			onUpdate: (self) => {
				if (!self.isActive) return;

				if (isAutoScrollingRef.current || disableScrollDetection.current) return;

				const progress = self.progress;
				const carlineIndex = Math.min(Math.floor(progress * numberOfCarlines), numberOfCarlines - 1);

				if (!carlines[carlineIndex]) return;

				setActiveCarlineIndex((prevIndex) => {
					if (carlineIndex !== prevIndex && carlineIndex >= 0) {
						setSelectedOferta(carlines[carlineIndex].ofertas[0]);

						// lógica de tracking
						if (!carlineBasicVehicleLoad.current.has(carlines[carlineIndex].code)) {
							trackOfertasVehicleLoad(carlines[carlineIndex]);
							carlineBasicVehicleLoad.current.add(carlines[carlineIndex].code);
						}
						return carlineIndex;
					}
					return prevIndex;
				});
			},
		});

		return () => {
			if (scrollTriggerRef.current) scrollTriggerRef.current.kill();
		};
	}, [carlines, numberOfCarlines, isScrollTriggerReady]);

	/* ---------------------------------------------------------------
       2. Reacción al cambio de activeCarlineIndex (Clicks / AutoScroll)
    ------------------------------------------------------------------ */
	useEffect(() => {
		if (activeCarlineIndex >= 0) {
			handleModelListAutoScroll(activeCarlineIndex, numberOfCarlines);

			if (carlineSelectedURLRef.current) {
				addCarlineInPath(activeCarlineIndex, carlines);
				carlineSelectedURLRef.current = false;
			} else if (!disableScrollDetection.current) {
				cleanCarlineInPath();
			}

			// cálculo de scroll nativo
			const viewportHeight = window.innerHeight;
			const rect = ofertasRef.current?.getBoundingClientRect(); // usamos el mismo rect para todos los carlines si hay más de uno
			if (!rect) {
				return;
			}

			const scrollY = window.scrollY;
			const ofertasRefTop = rect.top + scrollY;

			if (isFilterAnchorPendingRef.current) {
				return;
			}

			let scrollTo = ofertasRefTop;
			if (numberOfCarlines > 1) {
				const isLastCarline = activeCarlineIndex === numberOfCarlines - 1;
				scrollTo = isLastCarline
					? ofertasRefTop + viewportHeight * activeCarlineIndex
					: ofertasRefTop + viewportHeight * (activeCarlineIndex + 0.5);
			}

			// Validación de concordancia usando el progreso de GSAP
			if (scrollTriggerRef.current) {
				const currentScrollProgress = scrollTriggerRef.current.progress;
				const minProgress = (1 / numberOfCarlines) * activeCarlineIndex;
				const maxProgress = (1 / numberOfCarlines) * (activeCarlineIndex + 1);

				if (currentScrollProgress > minProgress && currentScrollProgress < maxProgress) {
					return;
				}
			}

			gsap.to(window, {
				duration: 0.5,
				scrollTo: { y: scrollTo },
				ease: 'power2.inOut',
				overwrite: 'auto', // detiene cualquier scroll en curso
				onStart: () => {
					isAutoScrollingRef.current = true;
				},
				onComplete: () => {
					isAutoScrollingRef.current = false;
				},
				onInterrupt: () => {
					isAutoScrollingRef.current = false;
				},
			});
		}
	}, [carlines, activeCarlineIndex]);

	/* ---------------------------------------------------------------
       3. Carga Inicial / Cambio de listado de Carlines (filtrado)
    ------------------------------------------------------------------ */
	useEffect(() => {
		if (!carlines || carlines.length === 0) {
			isFilterAnchorPendingRef.current = false;
			disableScrollDetection.current = false;
			return;
		}

		const prevNumberOfCarlines = prevNumberOfCarlinesRef.current;
		if (prevNumberOfCarlines !== numberOfCarlines) {
			prevNumberOfCarlinesRef.current = numberOfCarlines;
		}

		const carlineUrlSplitted = window.location.pathname.split('/');
		const carlineUrl = carlineUrlSplitted[carlineUrlSplitted.length - 1].replace('.app', '');
		const carlineIndex = carlines.findIndex((carline) => carline.name === carlineUrl);

		if (carlineIndex >= 0 && carlines[carlineIndex]) {
			setActiveCarlineIndex(carlineIndex);
			setSelectedOferta(carlines[carlineIndex].ofertas[0]);

			if (isFilterAnchorPendingRef.current) {
				requestAnimationFrame(() => {
					scrollOfertasToTop();
					ScrollTrigger.refresh();
					isFilterAnchorPendingRef.current = false;
					disableScrollDetection.current = false;
				});
				return;
			}

			if (hasCarlineInPath(carlines)) {
				disableScrollDetection.current = true;
				setTimeout(() => {
					disableScrollDetection.current = false;
					ScrollTrigger.refresh();
				}, 500);
			}
		} else {
			if (hasCarlineInPath(carlines)) {
				cleanCarlineInPath();
			}
			setActiveCarlineIndex(0);
			setSelectedOferta(carlines[0]?.ofertas?.[0]);

			if (isFilterAnchorPendingRef.current) {
				requestAnimationFrame(() => {
					scrollOfertasToTop();
					ScrollTrigger.refresh();
					isFilterAnchorPendingRef.current = false;
					disableScrollDetection.current = false;
				});
				return;
			}

			if (prevNumberOfCarlines !== numberOfCarlines && window.innerWidth < 960) {
				setTimeout(() => {
					const rect = ofertasRef.current?.getBoundingClientRect();
					if (rect) {
						window.scrollTo({ top: rect.top + window.scrollY - 50 });
					}
					ScrollTrigger.refresh();
				}, 100);
			} else {
				ScrollTrigger.refresh();
			}
		}
	}, [carlines]);

	return {
		activeCarlineIndex,
		setActiveCarlineIndex,
		selectedOferta,
		setSelectedOferta,
		ofertasRef,
		isAutoScrollingRef,
		disableScrollDetection,
		numberOfCarlines,
		carlineSelectedURLRef,
		anchorScrollOnFilter,
	};
};
